/**
 * Gift Tier System with Mobile Slider
 */

class GiftTierSystem {
  constructor() {
    this.container = document.querySelector('[data-gift-system]');
    if (!this.container) {
      return;
    }

    this.config = this.loadConfig();
    if (!this.config.enabled) {
      return;
    }

    this.progressBar = document.querySelector('[data-progress-fill]');
    this.progressText = document.querySelector('[data-progress-text]');
    this.tierCards = document.querySelectorAll('.gift-tier-card');
    
    this.currentCart = null;
    this.eligibleSubtotal = 0;
    this.currentTierIndex = -1;
    this.previousTierIndex = -1;
    this.selectedGift = null;
    this.sortedTiers = [...this.config.tiers].sort((a, b) => a.threshold - b.threshold);
    
    // Slider tracking
    this.giftSliders = {};
    this.isDrawer = this.container.classList.contains('drawer');
    this.isMobile = window.innerWidth <= 768;

    this.init();
  }

  loadConfig() {
    const configEl = document.querySelector('[data-gift-config]');
    if (!configEl) return { enabled: false };
    
    try {
      return JSON.parse(configEl.textContent);
    } catch (e) {
      return { enabled: false };
    }
  }

  init() {
    this.attachEventListeners();
    this.fetchCart();
    this.initSliders();
    this.handleResize();
    
    // Listen to cart events
    document.addEventListener('cart:updated', () => this.fetchCart());
    document.addEventListener('cart:refresh', () => this.fetchCart());
    document.addEventListener('cart:build', () => this.fetchCart());
  }

  attachEventListeners() {
    // Use event delegation for gift selection buttons
    document.addEventListener('click', (e) => {
      const selectButton = e.target.closest('[data-select-gift]');
      if (selectButton) {
        e.preventDefault();
        e.stopPropagation();
        this.handleGiftSelection(e);
      }
    });

    // Listen for cart quantity changes
    if (window.MutationObserver) {
      const cartObserver = new MutationObserver(() => {
        this.fetchCart();
      });
      
      const cartElement = document.querySelector('[data-cart-contents]') || 
                          document.querySelector('.cart-drawer') ||
                          document.querySelector('#cart-drawer');
      
      if (cartElement) {
        cartObserver.observe(cartElement, { 
          childList: true, 
          subtree: true 
        });
      }
    }
  }

  handleResize() {
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.innerWidth <= 768;
        
        // If changed from desktop to mobile or vice versa
        if (wasMobile !== this.isMobile) {
          if (this.isMobile) {
            this.initSliders();
          } else {
            this.destroySliders();
          }
        }
      }, 250);
    });
  }

  initSliders() {
    if (!this.isDrawer && !this.isMobile) {
      return;
    }

    const giftSliders = document.querySelectorAll('.gift-slider');
    
    giftSliders.forEach(slider => {
      const sliderId = slider.id;
      
      // Skip if already initialized
      if (this.giftSliders[sliderId] || slider.classList.contains('flickity-enabled')) {
        return;
      }
      
      // Only initialize if there are gift options
      const giftOptions = slider.querySelectorAll('.gift-option');
      if (giftOptions.length > 0) {
        this.initSlider(slider, sliderId);
      }
    });
  }

  destroySliders() {
    Object.keys(this.giftSliders).forEach(sliderId => {
      const sliderInstance = this.giftSliders[sliderId];
      
      if (sliderInstance) {
        // If using theme.Slideshow
        if (sliderInstance.flickity && typeof sliderInstance.flickity.destroy === 'function') {
          sliderInstance.flickity.destroy();
        }
        // If using Flickity directly
        else if (typeof sliderInstance.destroy === 'function') {
          sliderInstance.destroy();
        }
        
        delete this.giftSliders[sliderId];
      }
    });
  }

  reinitSliders() {
    if (!this.isDrawer && !this.isMobile) {
      return;
    }

    if (this.sliderReinitTimer) {
      clearTimeout(this.sliderReinitTimer);
    }

    this.sliderReinitTimer = setTimeout(() => {
      const giftSliders = document.querySelectorAll('.gift-slider');
      
      giftSliders.forEach(slider => {
        const sliderId = slider.id;
        const giftOptions = slider.querySelectorAll('.gift-option');
        
        if (giftOptions.length === 0) {
          if (this.giftSliders[sliderId]) {
            this.destroySlider(sliderId);
          }
          return;
        }
        
        if (this.giftSliders[sliderId] && slider.classList.contains('flickity-enabled')) {
          const sliderInstance = this.giftSliders[sliderId];
          if (sliderInstance.flickity && typeof sliderInstance.flickity.resize === 'function') {
            sliderInstance.flickity.resize();
          } else if (typeof sliderInstance.resize === 'function') {
            sliderInstance.resize();
          }
          return;
        }
        
        if (!this.giftSliders[sliderId]) {
          this.initSlider(slider, sliderId);
        }
      });
      
      this.sliderReinitTimer = null;
    }, 500);
  }

  initSlider(slider, sliderId) {
    // Use theme's Slideshow if available, otherwise use Flickity directly
    if (window.theme && window.theme.Slideshow) {
      this.giftSliders[sliderId] = new theme.Slideshow(slider, {
        cellAlign: 'left',
        contain: true,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: false,
        draggable: true,
        freeScroll: false,
        groupCells: true,
        adaptiveHeight: false
      });
    } else if (window.Flickity) {
      this.giftSliders[sliderId] = new Flickity(slider, {
        cellAlign: 'left',
        contain: true,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: false,
        draggable: true,
        freeScroll: false,
        groupCells: true
      });
    }
  }

  destroySlider(sliderId) {
    const sliderInstance = this.giftSliders[sliderId];
    
    if (sliderInstance) {
      // If using theme.Slideshow
      if (sliderInstance.flickity && typeof sliderInstance.flickity.destroy === 'function') {
        sliderInstance.flickity.destroy();
      }
      // If using Flickity directly
      else if (typeof sliderInstance.destroy === 'function') {
        sliderInstance.destroy();
      }
      
      delete this.giftSliders[sliderId];
    }
  }

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      this.currentCart = await response.json();
      this.calculateEligibility();
      await this.updateUI();
    } catch (error) {
      // Error fetching cart
    }
  }

  calculateEligibility() {
    if (!this.currentCart) return;

    const excludedProductIds = this.config.excludedProducts
      .map(id => parseInt(id.trim()))
      .filter(id => !isNaN(id));

    // Calculate eligible subtotal (excluding gifts and excluded products)
    this.eligibleSubtotal = 0;
    
    this.currentCart.items.forEach(item => {
      const isGift = this.isGiftItem(item);
      const isExcluded = excludedProductIds.includes(item.product_id);
      const itemTotal = (item.original_price * item.quantity) / 100;
      
      // Skip if it's a gift item or excluded product
      if (isGift || isExcluded) {
        return;
      }
      
      // Add to eligible subtotal
      this.eligibleSubtotal += itemTotal;
    });

    // Determine current tier index
    this.previousTierIndex = this.currentTierIndex;
    this.currentTierIndex = -1;
    
    for (let i = 0; i < this.sortedTiers.length; i++) {
      const tier = this.sortedTiers[i];
      if (this.eligibleSubtotal >= tier.threshold) {
        this.currentTierIndex = i;
      } else {
        break;
      }
    }
  }

  isGiftItem(item) {
    // First: Check if properties indicate it's a gift
    if (item.properties) {
      if (item.properties._gift_item === 'true' || item.properties._gift_item === true) {
        return true;
      }
    }
    
    // Second: Check line_item properties (sometimes Shopify puts it here)
    if (item.line_item_properties) {
      const giftProp = item.line_item_properties.find(prop => prop.name === '_gift_item');
      if (giftProp && (giftProp.value === 'true' || giftProp.value === true)) {
        return true;
      }
    }
    return false;
  }

  getAllGiftVariantIds() {
    const giftOptions = document.querySelectorAll('[data-gift-option], [data-gift-item]');
    const variantIds = [];
    
    giftOptions.forEach(option => {
      // For automatic gifts, look inside for the hidden div
      let variantId;
      if (option.hasAttribute('data-gift-item')) {
        const hiddenDiv = option.querySelector('[data-variant-id]');
        variantId = hiddenDiv ? parseInt(hiddenDiv.dataset.variantId) : null;
      } else {
        variantId = parseInt(option.dataset.variantId);
      }
      
      if (variantId && !variantIds.includes(variantId)) {
        variantIds.push(variantId);
      }
    });
    
    return variantIds;
  }

  async updateUI() {
    this.updateProgressBar();
    await this.updateVisibleTiers();
    await this.handleTierTransition();
    this.updateTierCards();
    await this.checkAutoGifts();

    this.reinitSliders();
  }

  updateProgressBar() {
    if (!this.progressBar || !this.progressText) return;

    // If no tiers configured
    if (this.sortedTiers.length === 0) {
      this.progressBar.style.width = '0%';
      this.progressText.textContent = 'No tiers configured';
      return;
    }

    const nextTierIndex = this.currentTierIndex + 1;
    
    // If maximum tier reached
    if (nextTierIndex >= this.sortedTiers.length) {
      this.progressBar.style.width = '100%';
      this.progressText.textContent = '🎉 Maximum tier reached!';
      return;
    }

    const nextTier = this.sortedTiers[nextTierIndex];
    const previousThreshold = this.currentTierIndex >= 0 
      ? this.sortedTiers[this.currentTierIndex].threshold 
      : 0;
    
    // Calculate progress percentage
    const tierRange = nextTier.threshold - previousThreshold;
    const progress = ((this.eligibleSubtotal - previousThreshold) / tierRange) * 100;
    const clampedProgress = Math.max(0, Math.min(100, progress));
    
    this.progressBar.style.width = clampedProgress + '%';

    // Calculate remaining amount
    const remaining = Math.max(0, nextTier.threshold - this.eligibleSubtotal);
    
    if (remaining > 0) {
      const formattedAmount = this.formatMoney(remaining);
      const text = this.config.progressBarText
        .replace('{amount}', formattedAmount)
        .replace('{tier}', nextTier.label);
      this.progressText.textContent = text;
    } else {
      const text = this.config.unlockedText
        .replace('{tier}', nextTier.label);
      this.progressText.textContent = text;
    }
  }

  async updateVisibleTiers() {
    const tiersToShow = [];
    
    if (this.currentTierIndex >= 0) {
      if (this.currentTierIndex === this.sortedTiers.length - 1) {
        tiersToShow.push(this.currentTierIndex);
      } else {
        tiersToShow.push(this.currentTierIndex);
        if (this.currentTierIndex + 1 < this.sortedTiers.length) {
          tiersToShow.push(this.currentTierIndex + 1);
        }
      }
    } else {
      if (this.sortedTiers.length > 0) tiersToShow.push(0);
      if (this.sortedTiers.length > 1) tiersToShow.push(1);
    }

    this.tierCards.forEach(card => {
      card.style.display = 'none';
    });

    tiersToShow.forEach(index => {
      const tier = this.sortedTiers[index];
      const card = this.findTierCard(tier.tier);
      if (card) {
        card.style.display = 'block';
      }
    });
  }

  findTierCard(tierNumber) {
    return Array.from(this.tierCards).find(card => 
      parseInt(card.dataset.tier) === tierNumber
    );
  }

  async handleTierTransition() {
    let giftsRemoved = false;
    
    // Case 1: Tier increased
    if (this.previousTierIndex !== this.currentTierIndex && 
        this.previousTierIndex >= 0 && 
        this.currentTierIndex > this.previousTierIndex) {
      
      const previousTier = this.sortedTiers[this.previousTierIndex];
      await this.removeTierGift(previousTier);
      giftsRemoved = true;
    }
    
    // Case 2: Tier decreased
    if (this.previousTierIndex !== this.currentTierIndex && 
        this.previousTierIndex >= 0 && 
        this.currentTierIndex < this.previousTierIndex) {
      
      for (let i = this.previousTierIndex; i > this.currentTierIndex; i--) {
        const lockedTier = this.sortedTiers[i];
        await this.removeTierGift(lockedTier);
      }
      giftsRemoved = true;
    }
    
    // Case 3: User removed everything
    if (this.currentTierIndex === -1 && this.previousTierIndex >= 0) {
      for (let i = 0; i <= this.previousTierIndex; i++) {
        const tier = this.sortedTiers[i];
        await this.removeTierGift(tier);
      }
      giftsRemoved = true;
    }
    
    if (giftsRemoved) {
      // Try to refresh cart drawer specifically
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        const response = await fetch('/cart.js');
        const cartData = await response.json();
        cartDrawer.renderContents(cartData);
      }
      
      // Also try theme method
      if (window.theme && window.theme.Cart && typeof window.theme.Cart.get === 'function') {
        window.theme.Cart.get();
      }
      
      // Wait for DOM to update
      await new Promise(resolve => setTimeout(resolve, 600));
    }
  }

  async removeTierGift(tier) {
    const giftItems = this.currentCart.items.filter(item => {
      if (!this.isGiftItem(item)) return false;
      if (item.properties && item.properties._gift_tier === tier.label) {
        return true;
      }
      return false;
    });

    for (const giftItem of giftItems) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 0
          })
        });
      } catch (error) {
        // Error removing gift
      }
    }
    
    await this.fetchCart();
    document.dispatchEvent(new CustomEvent('cart:build'));
    await new Promise(resolve => setTimeout(resolve, 300));
  }

  updateTierCards() {
    this.tierCards.forEach(card => {
      const tierNum = parseInt(card.dataset.tier);
      const threshold = parseFloat(card.dataset.threshold);
      const mode = card.dataset.mode;
      const statusEl = card.querySelector('[data-tier-status]');
      
      const isUnlocked = this.eligibleSubtotal >= threshold;
      
      // Update card state
      card.classList.toggle('unlocked', isUnlocked);
      card.classList.toggle('locked', !isUnlocked);
      
      // Update status badge
      if (statusEl) {
        statusEl.className = 'tier-status ' + (isUnlocked ? 'unlocked' : 'locked');
        statusEl.textContent = isUnlocked ? '✓ Unlocked' : '🔒 Locked';
      }
      
      // Disable/Enable gift selection buttons based on tier lock status
      const selectButtons = card.querySelectorAll('[data-select-gift]');
      selectButtons.forEach(btn => {
        if (!isUnlocked) {
          btn.disabled = true;
          btn.classList.add('locked-btn');
        } else {
          // Only enable if not already selected
          const isSelected = btn.classList.contains('selected');
          btn.disabled = isSelected;
          btn.classList.remove('locked-btn');
        }
      });
      
      // Check if this tier already has a gift in cart
      if (isUnlocked && mode === 'choice') {
        this.checkSelectedGift(card);
      }
    });
  }

  checkSelectedGift(tierCard) {
    const giftOptions = tierCard.querySelectorAll('[data-gift-option]');
    
    giftOptions.forEach(option => {
      const variantId = parseInt(option.dataset.variantId);
      const quantity = parseInt(option.dataset.quantity) || 1;
      
      // Check if this exact gift with correct quantity exists in cart
      const hasGift = this.currentCart.items.some(item => 
        item.variant_id === variantId && 
        this.isGiftItem(item) &&
        item.quantity >= quantity &&
        (item.properties._gift_item === 'true' || item.properties._gift_item === true)
      );
      
      const btn = option.querySelector('[data-select-gift]');
      if (btn) {
        if (hasGift) {
          btn.textContent = '✓ Selected';
          btn.classList.add('selected');
          btn.disabled = true;
          option.classList.add('selected');
        } else {
          btn.textContent = 'Select Gift';
          btn.classList.remove('selected');
          btn.disabled = false;
          option.classList.remove('selected');
        }
      }
    });
  }

  async checkAutoGifts() {
    // Handle automatic mode tiers
    const visibleAutoTiers = Array.from(this.tierCards)
      .filter(card => card.style.display !== 'none' && card.dataset.mode === 'automatic');
    
    for (const tierCard of visibleAutoTiers) {
      const threshold = parseFloat(tierCard.dataset.threshold);
      const isUnlocked = this.eligibleSubtotal >= threshold;
      
      if (isUnlocked) {
        await this.ensureAutoGift(tierCard);
      }
    }
  }

  async ensureAutoGift(tierCard) {
    const tierLabel = tierCard.dataset.label;
    const giftItem = tierCard.querySelector('[data-gift-item]');
    
    if (!giftItem) return;
    
    // Get data from hidden div
    const hiddenDiv = giftItem.querySelector('[data-variant-id]');
    if (!hiddenDiv) return;
    
    const variantId = parseInt(hiddenDiv.dataset.variantId);
    const quantity = parseInt(hiddenDiv.dataset.quantity) || 1;
    const productTitle = hiddenDiv.dataset.productTitle;
    const isAvailable = hiddenDiv.dataset.available === 'true';
    
    if (!variantId || !isAvailable) return;
    
    // Check if this tier already has its gift in cart with correct quantity
    const hasGift = this.currentCart.items.some(item => 
      this.isGiftItem(item) && 
      item.properties && 
      item.properties._gift_tier === tierLabel &&
      item.variant_id === variantId &&
      item.quantity >= quantity
    );
    
    if (hasGift) {
      return;
    }
    
    try {
      await this.addGiftToCart(variantId, productTitle, tierLabel, quantity);
      this.showNotification(`${productTitle} automatically added!`);
    } catch (error) {
      // Error auto-adding gift
    }
  }

  async handleGiftSelection(e) {
    const btn = e.target.closest('[data-select-gift]');
    const giftOption = btn ? btn.closest('[data-gift-option]') : null;
    
    if (!giftOption) {
      return;
    }
    
    // Find which tier this gift belongs to
    const tierCard = giftOption.closest('.gift-tier-card');
    const tierLabel = tierCard ? tierCard.dataset.label : 'unknown';
    const threshold = tierCard ? parseFloat(tierCard.dataset.threshold) : 0;
    
    // Check if tier is unlocked
    if (this.eligibleSubtotal < threshold) {
      alert(`This tier is locked. Spend ${this.formatMoney(threshold - this.eligibleSubtotal)} more to unlock it.`);
      return;
    }
    
    const variantId = parseInt(giftOption.dataset.variantId);
    const quantity = parseInt(giftOption.dataset.quantity) || 1;
    const productTitle = giftOption.dataset.productTitle;
    const isAvailable = giftOption.dataset.available === 'true';
    
    if (!isAvailable) {
      alert('This gift is currently out of stock');
      return;
    }
    
    if (!variantId) {
      alert('Error: No variant ID found for this gift');
      return;
    }
    
    // Disable button during request
    btn.disabled = true;
    btn.textContent = 'Adding...';
    
    try {
      // Remove any existing gift from this tier first
      await this.removeCurrentTierGift(tierLabel);
      
      // Add the selected gift with quantity
      await this.addGiftToCart(variantId, productTitle, tierLabel, quantity);
      
      // Show success message
      this.showNotification(this.config.giftAddedText);
      
      // Refresh cart data
      await this.fetchCart();
      
      // Trigger cart rebuild
      document.dispatchEvent(new CustomEvent('cart:build'));
      
      // Fire analytics event
      this.trackGiftSelection(productTitle, variantId, tierLabel, quantity);
      
    } catch (error) {
      alert('Error adding gift to cart. Please try again.');
      btn.disabled = false;
      btn.textContent = 'Select Gift';
    }
  }

  async removeCurrentTierGift(tierLabel) {
    const giftItems = this.currentCart.items.filter(item => {
      if (!this.isGiftItem(item)) return false;
      if (item.properties && item.properties._gift_tier === tierLabel) {
        return true;
      }
      return false;
    });

    for (const giftItem of giftItems) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: giftItem.key,
          quantity: 0
        })
      });
    }
    
    await new Promise(resolve => setTimeout(resolve, 300));
  }

  async addGiftToCart(variantId, productTitle, tierLabel, quantity = 1) {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{
          id: variantId,
          quantity: quantity,
          properties: {
            '_gift_item': 'true',
            '_gift_tier': tierLabel,
            '_gift_sku': productTitle
          }
        }]
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.description || 'Failed to add gift to cart');
    }
    
    return await response.json();
  }

  showNotification(message) {
    const notification = document.createElement('div');
    notification.className = 'gift-notification';
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #2ecc71;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.15);
      z-index: 10000;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.style.animation = 'slideOut 0.3s ease';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  trackGiftSelection(productTitle, variantId, tierLabel, quantity) {
    // Google Analytics 4
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'gift_selected',
        gift_name: productTitle,
        gift_variant_id: variantId,
        gift_quantity: quantity,
        tier: tierLabel,
        cart_total: this.eligibleSubtotal
      });
    }
    
    // Klaviyo
    if (window._learnq) {
      window._learnq.push(['track', 'Gift Selected', {
        gift_name: productTitle,
        gift_variant_id: variantId,
        gift_quantity: quantity,
        tier: tierLabel,
        cart_total: this.eligibleSubtotal
      }]);
    }
  }

  formatMoney(amount) {
    return '$' + amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new GiftTierSystem();
  });
} else {
  new GiftTierSystem();
}